import { ButtonWithLoading, ErrorModal, MesfModal } from "@dexteel/mesf-core";
import { Checkbox, FormControlLabel, TextField } from "@mui/material";
import Button from "@mui/material/Button";
import Grid2 from "@mui/material/Grid2";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/lab/Alert";
import { type SyntheticEvent, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import type { Option } from "../../../models/Option";
import { useFormsContext } from "../../../context/FormsContext";
import { upsertOption } from "../../../repositories/FormsRepository";
import { INITIAL_VALUES_OPTION } from "../../../models/OptionInitialValue";
import { OptionTypePicker } from "./optionTypePicker";

type Props = {
	show: boolean;
	onHide: (shouldUpdate: boolean) => void;
	setShouldUpdateWhenExit: (shouldUpdate: boolean) => void;
	data: Option | null;
	newMode?: boolean;
	questionIdToSearchOptions: number;
};

export const NewAndEditOptionModal = ({
	show,
	onHide,
	data,
	newMode = false,
	setShouldUpdateWhenExit,
	questionIdToSearchOptions,
}: Props) => {
	const {
		state: { optionSelectedInTable, OptionId },
		actions: {
			setOptionTextInTable,
			setOptionIsEnabledInTable,
			setOptionOptionTypeCodeInTable,
		},
	} = useFormsContext();

	const [editedSuccess, setEditedSuccess] = useState(false);
	const [isSubmitLoading, setIsSubmitLoading] = useState(false);

	const [error, setError] = useState<string>("");
	const [deleteByIdError, setDeleteByIdError] = useState<string>("");
	const {
		control,
		handleSubmit,
		watch,
		setValue,
		formState: { errors },
		reset,
	} = useForm<Option>({ defaultValues: INITIAL_VALUES_OPTION });

	const handleClose = (
		event: Event | React.SyntheticEvent,
		reason?: string,
	) => {
		if (reason === "clickaway") {
			return;
		}
		setEditedSuccess(false);
	};
	const handleCancel = async () => {
		reset();
		onHide(false);
	};

	const optionText = watch("OptionText");

	const changeOptionText = () => {
		setOptionTextInTable(optionText);
	};

	const isRequired = watch("IsEnabled");

	const changeIsRequired = () => {
		setOptionIsEnabledInTable(isRequired);
	};
	const includesComment = watch("OptionTypeCode");

	const changeIncludesComment = () => {
		setOptionOptionTypeCodeInTable(includesComment);
	};

	const onSubmit = async (optionData: Option) => {
		setIsSubmitLoading(true);
		const res = await upsertOption(
			OptionId,
			questionIdToSearchOptions,
			optionData.OptionText,
			optionData.OptionTypeCode,
			optionData.IsEnabled,
		);
		if (res.ok) {
			reset();
			setIsSubmitLoading(false);
			setEditedSuccess(true);
			setShouldUpdateWhenExit(true);
			onHide(true);
		} else {
			setIsSubmitLoading(false);
			setError(res.message);
		}
	};

	useEffect(() => {
		if (data != null && show) {
			reset(INITIAL_VALUES_OPTION);
			setOptionTextInTable(data.OptionText);
			setOptionIsEnabledInTable(data.IsEnabled);
			setOptionOptionTypeCodeInTable(data.OptionTypeCode);
			setValue("OptionText", data.OptionText);
			setValue("IsEnabled", data.IsEnabled);
			setValue("OptionTypeCode", data.OptionTypeCode);
			setIsSubmitLoading(false);
		} else {
			reset(INITIAL_VALUES_OPTION);
		}
	}, [show]);

	useEffect(() => {
		changeOptionText();
	}, [optionText]);

	useEffect(() => {
		changeIsRequired();
	}, [isRequired]);

	useEffect(() => {
		changeIncludesComment();
	}, [includesComment]);

	return (
		<>
			<MesfModal
				title={newMode ? "NEW OPTION" : "EDIT OPTION"}
				open={show}
				handleClose={() => handleCancel()}
				maxWidth="sm"
				id="form-option-modal"
			>
				<form onSubmit={handleSubmit(onSubmit)}>
					<MesfModal.Content dividers>
						<Grid2 container spacing={2}>
							<Grid2 size={12}>
								<Controller
									name="OptionText"
									control={control}
									rules={{
										validate: (value) => value !== "",
									}}
									render={({ field: { onChange, value } }) => (
										<TextField
											label="Option Name"
											variant="outlined"
											fullWidth
											error={!!errors.OptionText}
											margin="dense"
											autoComplete="off"
											value={value ?? ""}
											onChange={(e) => onChange(e)}
											slotProps={{ inputLabel: { shrink: true } }}
										/>
									)}
								/>
							</Grid2>
							<Grid2 size={{ md: 6, xs: 12 }}>
								<Controller
									name="OptionTypeCode"
									control={control}
									render={({ field: { onChange, value } }) => (
										<OptionTypePicker
											value={value as string}
											onChange={onChange}
										/>
									)}
								/>
							</Grid2>
							<Grid2
								size={{ md: 6, xs: 12 }}
								sx={{ display: "flex", alignItems: "center" }}
							>
								<Controller
									name="IsEnabled"
									control={control}
									defaultValue={optionSelectedInTable?.IsEnabled}
									render={({ field }) => (
										<FormControlLabel
											sx={{ userSelect: "none" }}
											control={
												<Checkbox
													checked={field.value as boolean}
													onChange={(e) => field.onChange(e.target.checked)}
													name="IsEnabled"
													color="primary"
												/>
											}
											label="Is Enabled"
										/>
									)}
								/>
							</Grid2>
						</Grid2>
					</MesfModal.Content>
					<MesfModal.Actions style={{ padding: "20px 16px 16px" }}>
						<Grid2 container spacing={2} justifyContent="flex-end" width="100%">
							<Grid2 size={{ md: 3, xs: 12 }}>
								<Button
									fullWidth
									variant="contained"
									color="inherit"
									onClick={handleCancel}
								>
									Cancel
								</Button>
							</Grid2>
							<Grid2 size={{ md: 3, xs: 12 }}>
								<ButtonWithLoading
									fullWidth
									isLoading={isSubmitLoading}
									variant="contained"
									color="primary"
									type="submit"
								>
									Save
								</ButtonWithLoading>
							</Grid2>
						</Grid2>
					</MesfModal.Actions>
				</form>
			</MesfModal>
			<Snackbar
				open={editedSuccess}
				autoHideDuration={2500}
				onClose={handleClose}
				anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
			>
				<Alert severity="info" onClose={handleClose}>
					Success
				</Alert>
			</Snackbar>
			<ErrorModal error={error} onHide={() => setError("")} />
			<ErrorModal
				error={deleteByIdError}
				onHide={() => setDeleteByIdError("")}
			/>
		</>
	);
};
